import { useIntl } from 'react-intl';

import { EntityPacingStatus } from '../EntityPacingStatus';

import type { DistributedGoals, MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { LineItemPacing } from '../EntityPacingStatus/PacingStatusTable/PacingStatusTable';

type Props = {
  currencyInfo: BaseCurrencyInterface;
  entityPacing: LineItemPacing | null;
};

export function LineItemPacingStatus({ currencyInfo, entityPacing }: Props) {
  const { formatMessage } = useIntl();
  const lineItemId = entityPacing?.lineItemId ?? '';

  const getLineItemPacings = (distributionType: DistributedGoals['distributionType']) => {
    const distributionTypeKey = distributionType === 'creat' ? 'creativeGoals' : 'locationGoals';

    return (
      (entityPacing as LineItemPacing)?.distributedGoals
        ?.find(dg => dg.distributionType === distributionType)
        ?.goals?.map(goal => {
          const { globalGoal, goalDistribution } = goal;
          return {
            ...globalGoal,
            childrenPacingStatus: goalDistribution.map((gd, index) => {
              return {
                ...gd,
                currentDays: 0,
                daysPct: 0,
                goalType: gd.type,
                id: `${distributionTypeKey}_${index}_${lineItemId}`,
                totalDays: 0
              };
            }),
            currentDays: entityPacing?.currentDays ?? 0,
            daysPct: entityPacing?.daysPct ?? 0,
            goalType: globalGoal.type,
            id: lineItemId,
            name: formatMessage({
              defaultMessage: globalGoal.type.charAt(0).toUpperCase() + globalGoal.type.slice(1),
              id: `pacingStatusModal.content.lineItem.${distributionTypeKey}.totalRowTitle.${globalGoal.type}`
            }),
            totalDays: entityPacing?.totalDays ?? 0,
            type: 'lineItem' as MultiFlightEntityType
          };
        }) || []
    );
  };

  return (
    <>
      <EntityPacingStatus
        currencyInfo={currencyInfo}
        entityPacing={{
          id: lineItemId,
          isComposed: true,
          pacings:
            entityPacing?.globalGoals?.map(goal => {
              return {
                ...goal,
                currentDays: entityPacing?.currentDays ?? 0,
                daysPct: entityPacing?.daysPct ?? 0,
                goalType: goal.type,
                id: `globalGoal_${goal.type}_${lineItemId}`,
                name: formatMessage({
                  defaultMessage: goal.type.charAt(0).toUpperCase() + goal.type.slice(1),
                  id: `pacingStatusModal.content.lineItem.globalGoals.totalRowTitle.${goal.type}`
                }),
                totalDays: entityPacing?.totalDays ?? 0,
                type: 'lineItem'
              };
            }) ?? [],
          type: 'lineItem'
        }}
        headerName={formatMessage({
          defaultMessage: 'Goal Type',
          id: 'pacingStatusModal.content.lineItemTable.globalGoals.headerName'
        })}
        includeDatesColumn
        includeTimeframe
        omitExpandables
        title={formatMessage({
          defaultMessage: 'Global Goals',
          id: 'pacingStatusModal.content.lineItemTable.globalGoals.title'
        })}
      />
      {!!(entityPacing as LineItemPacing)?.distributedGoals?.length && (
        <>
          <EntityPacingStatus
            currencyInfo={currencyInfo}
            entityPacing={{
              id: `creatGoals_${lineItemId}`,
              isComposed: true,
              pacings: getLineItemPacings('creat'),
              type: 'lineItem'
            }}
            headerName={formatMessage({
              defaultMessage: 'Creative',
              id: 'pacingStatusModal.content.lineItemTable.creativeGoals.headerName'
            })}
            includeDatesColumn
            includeTimeframe
            title={formatMessage({
              defaultMessage: 'Creative Goals',
              id: 'pacingStatusModal.content.lineItemTable.creativeGoals.title'
            })}
          />
          <EntityPacingStatus
            currencyInfo={currencyInfo}
            entityPacing={{
              id: `locationGoals_${lineItemId}`,
              isComposed: true,
              pacings: getLineItemPacings('location'),
              type: 'lineItem'
            }}
            headerName={formatMessage({
              defaultMessage: 'Group Name',
              id: 'pacingStatusModal.content.lineItemTable.locationGoals.headerName'
            })}
            includeDatesColumn
            includeTimeframe
            title={formatMessage({
              defaultMessage: 'Location Group Goals',
              id: 'pacingStatusModal.content.lineItemTable.locationGoals.title'
            })}
          />
        </>
      )}
    </>
  );
}
